'use client';

import { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import React, { useEffect, useCallback } from 'react';

import {
  PrevButton,
  NextButton,
  usePrevNextButtons
} from '../components/Arrows/Arrows';
import { DotButton, useDotButton } from '../components/Dot/Dot';
import { useNestedCarousel } from '../hooks';

import { styles } from './UiKitCarouselBase.styles';

export interface UiKitCarouselBaseProps {
  className?: string;
  autoplay?: boolean | number;
  fade?: boolean;
  showArrows?: boolean;
  showDots?: boolean;
  children?: React.ReactNode;
  dotsClass?: string;
  arrowPrevClass?: string;
  arrowNextClass?: string;
  arrowType?: 'arrow' | 'chevron' | 'chevronBold';
  options?: EmblaOptionsType;
  onSlidesChange?: (index: number) => void;
  loop?: boolean;
  viewportClassName?: string;
  containerClassName?: string;
  isNested?: boolean;
  dragFree?: boolean;
  renderButtons?: (buttons: React.ReactNode) => React.ReactNode;
  renderCustomControls?: (
    controls: ReturnType<typeof usePrevNextButtons>
  ) => React.ReactNode;
  activeDotClass?: string;
  withScrollGestures?: boolean;
  onInit?: (api: EmblaCarouselType) => void;
  skipSnaps?: boolean;
  slidesToScroll?: number;
  classNameIcon?: string;
  controlsBottom?: boolean;
}

export function UiKitCarouselBase({
  className,
  autoplay = false,
  fade = false,
  showArrows = true,
  showDots = false,
  dotsClass,
  arrowPrevClass,
  arrowNextClass,
  arrowType,
  children,
  options,
  onSlidesChange,
  loop = true,
  viewportClassName,
  containerClassName,
  isNested = false,
  dragFree = false,
  withScrollGestures = false,
  renderButtons,
  activeDotClass,
  renderCustomControls,
  onInit,
  skipSnaps = false,
  slidesToScroll = 1,
  classNameIcon,
  controlsBottom
}: UiKitCarouselBaseProps) {
  const slots = styles({});
  const { watchDrag, setupNestedHandlers } = useNestedCarousel(isNested);

  const defaultOptions: EmblaOptionsType = {
    loop,
    align: 'start',
    watchDrag,
    dragFree: dragFree,
    skipSnaps,
    slidesToScroll
  };

  const combinedOptions: EmblaOptionsType = { ...defaultOptions, ...options };

  const plugins = [];

  if (autoplay) {
    const delay = autoplay === true ? 5000 : Number(autoplay);
    plugins.push(Autoplay({ delay }));
  }
  if (fade) {
    plugins.push(Fade());
  }
  if (withScrollGestures) {
    plugins.push(WheelGesturesPlugin());
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(combinedOptions, plugins);

  useEffect(() => {
    if (!emblaApi) return;
    onInit?.(emblaApi);
    return setupNestedHandlers(emblaApi);
  }, [emblaApi, setupNestedHandlers, onInit]);

  const onNavButtonClick = useCallback(() => {
    if (emblaApi && emblaApi?.plugins()?.autoplay) {
      emblaApi.plugins()?.autoplay?.reset();
    }
  }, [emblaApi]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick
  );

  const controls = usePrevNextButtons(emblaApi, onNavButtonClick);
  const controlsVisible =
    options?.active || !controls.prevBtnDisabled || !controls.nextBtnDisabled;

  useEffect(() => {
    if (onSlidesChange) {
      onSlidesChange(selectedIndex);
    }
  }, [selectedIndex, onSlidesChange]);
  const buttonsNode = (
    <>
      {showArrows && !controls.prevBtnDisabled && (
        <PrevButton
          className={slots.arrow({ className: arrowPrevClass })}
          classNameIcon={classNameIcon}
          onClick={controls.onPrevButtonClick}
          arrowType={arrowType}
        />
      )}

      {showArrows && !controls.nextBtnDisabled && (
        <NextButton
          className={slots.arrow({ className: arrowNextClass })}
          classNameIcon={classNameIcon}
          onClick={controls.onNextButtonClick}
          arrowType={arrowType}
        />
      )}
    </>
  );
  const childrenArray = React.Children.toArray(children);

  const slides =
    emblaApi && childrenArray.length < emblaApi.slidesInView().length + 1
      ? Array.from(
          { length: emblaApi.slidesInView().length * 2 },
          (_, index) => childrenArray[index % childrenArray.length]
        )
      : childrenArray;

  return (
    <div className={slots.base({ className })}>
      <div
        className={slots.viewport({ className: viewportClassName })}
        ref={emblaRef}
      >
        <div className={slots.container({ className: containerClassName })}>
          {slides.map((child, index) =>
            React.isValidElement(child) ? (
              <div
                key={index}
                className={slots.slide()}
              >
                {child}
              </div>
            ) : null
          )}
        </div>
      </div>

      {controlsBottom ? (
        <div className={slots.bottomControls()}>
          <div className={slots.bottomControlsDots()}>
            {controlsVisible && showDots && (
              <div className={slots.bottomDots({ className: dotsClass })}>
                {scrollSnaps.length > 1 &&
                  scrollSnaps.map((_, index) => (
                    <DotButton
                      key={index}
                      onClick={() => onDotButtonClick(index)}
                      className={slots.bottomDot({
                        active: index === selectedIndex,
                        className: index === selectedIndex ? activeDotClass : ''
                      })}
                    />
                  ))}
              </div>
            )}
          </div>
          <div className={slots.bottomControlsButtons()}>
            {renderButtons?.(buttonsNode) ?? buttonsNode}
          </div>
        </div>
      ) : (
        <>
          {renderButtons?.(buttonsNode) ?? buttonsNode}
          {renderCustomControls?.(controls)}

          {controlsVisible && showDots && (
            <div className={slots.dots({ className: dotsClass })}>
              {scrollSnaps.length > 1 &&
                scrollSnaps.map((_, index) => (
                  <DotButton
                    key={index}
                    onClick={() => onDotButtonClick(index)}
                    className={slots.dot({
                      active: index === selectedIndex,
                      className: index === selectedIndex ? activeDotClass : ''
                    })}
                  />
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
